import type { Invoice, InvoiceCheckStatus } from '@/types/portal';

const statusStyles: Record<
  InvoiceCheckStatus,
  { icon: string; wrap: string; iconColor: string; label: string }
> = {
  pass: {
    icon: 'ri-check-line',
    wrap: 'border-accent-200 bg-accent-50',
    iconColor: 'text-accent-700',
    label: 'Passed',
  },
  warn: {
    icon: 'ri-alert-line',
    wrap: 'border-secondary-200 bg-secondary-50',
    iconColor: 'text-secondary-800',
    label: 'Check',
  },
  fail: {
    icon: 'ri-error-warning-line',
    wrap: 'border-primary-200 bg-primary-50',
    iconColor: 'text-primary-700',
    label: 'Blocking',
  },
};

export default function InvoiceValidation({ invoice }: { invoice: Invoice }) {
  const fails = invoice.checks.filter((check) => check.status === 'fail').length;
  const warns = invoice.checks.filter((check) => check.status === 'warn').length;

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center justify-between gap-3 border-b border-background-200 px-5 py-4">
        <div>
          <h2 className="font-heading text-sm font-semibold text-foreground-950">
            Pre-approval checks
          </h2>
          <p className="mt-0.5 text-xs text-foreground-500">
            Automated validation of deliverables, rate, compliance and payout readiness
          </p>
        </div>
        <span className="text-xs font-label text-foreground-500">
          {invoice.checks.length} checks
        </span>
      </header>

      <div className="divide-y divide-background-100">
        {invoice.checks.map((check) => {
          const style = statusStyles[check.status];
          return (
            <div key={check.id} className="flex items-start gap-3 px-5 py-3.5">
              <span
                className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border ${style.wrap} ${style.iconColor}`}
              >
                <i className={style.icon} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-foreground-900">{check.label}</p>
                  <span className={`text-[11px] font-label font-medium ${style.iconColor}`}>
                    {style.label}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-foreground-600">{check.detail}</p>
              </div>
            </div>
          );
        })}
      </div>

      {fails === 0 && warns === 0 ? (
        <div className="border-t border-background-200 px-5 py-3">
          <p className="flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-shield-check-line" />
            All checks passed. This invoice is ready to approve.
          </p>
        </div>
      ) : null}
    </section>
  );
}